import { useEntityFilter } from '@risksmart-app/components/src/contexts/entityFilterContext';
import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import type { ControlDashboardDateField } from '@risksmart-app/domain/src/types/consts/dashboard-date-fields';
import type { ControlRegisterItem } from '@risksmart-app/drizzle/src/dto';
import type { ControlRegisterResponse } from '@risksmart-app/trpc/src/types';
import type {
  Control_Bool_Exp,
  GetControlsQuery,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  GetControlsDocument,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook, type StrictMappedGraphQLSelection } from 'src/utils';

import { CONTROL_DATE_PREDICATES } from '@/hooks/queries/dashboardDatePredicates';
import {
  buildDashboardWhere,
  type DashboardFilterArgs,
  departmentsWhereOnEntity,
  normaliseArrayFilter,
  tagsWhereOnEntity,
} from '@/hooks/queries/dashboardFilterArgs';
import useEntityWhereFilter from '@/hooks/useEntityWhereFilter';
import type { GraphQLFetchPolicy } from '@/utils/hook-factories/types';

type InternalArgs = UseGetControlsRegisterArgs & {
  entityIds?: string[];
};

const useControlsGraphqlVariables = (args: InternalArgs) => {
  const { parentId } = args;
  const { user } = useRisksmartUser();
  const entityFilter = useEntityWhereFilter<Control_Bool_Exp>(
    Parent_Type_Enum.Control
  );

  // `parentId` overrides the entity filter entirely — the parent-scoped call
  // site is a detail panel, not a register, and that precedence predates COI-691.
  const base: Control_Bool_Exp = parentId
    ? { parents: { ParentId: { _eq: parentId } } }
    : entityFilter;

  const { where } = buildDashboardWhere({
    base,
    filters: args,
    userId: user?.userId,
    datePredicates: CONTROL_DATE_PREDICATES,
    tagsWhere: tagsWhereOnEntity,
    departmentsWhere: departmentsWhereOnEntity,
    ownershipWhere: (conditions) => conditions.controlFilterConditions,
  });

  return { where };
};

export function mapTrpcControlToGraphQL(
  control: ControlRegisterItem
): StrictMappedGraphQLSelection<GetControlsQuery['control'][number]> {
  return {
    ...control,
    actions_aggregate: {
      aggregate: {
        count: control.openActionCount ?? 0,
      },
    },
    issues_aggregate: {
      aggregate: {
        count: control.issueCount ?? 0,
      },
    },
    open_issue_aggregate: {
      aggregate: {
        count: control.openIssueCount ?? 0,
      },
    },
    indicators_aggregate: {
      aggregate: {
        count: control.indicatorCount ?? 0,
      },
    },
  };
}

const useGetControlsRegisterBase = createQueryHook<
  InternalArgs,
  ControlRegisterResponse,
  GetControlsQuery
>({
  trpcQueryOptions: (
    trpc,
    { parentId, entityIds, tagTypeIds, departmentTypeIds, dateRange, ownership }
  ) =>
    trpc.frontend.control.register.queryOptions({
      parentId,
      entityIds,
      tagTypeIds: normaliseArrayFilter(tagTypeIds),
      departmentTypeIds: normaliseArrayFilter(departmentTypeIds),
      dateRange,
      ownership,
    }),
  mapTrpcDataToGraphQL: (data) => ({
    control: data.map((control) => mapTrpcControlToGraphQL(control)),
  }),
  graphqlDocument: GetControlsDocument,
  graphqlVariables: useControlsGraphqlVariables,
});

export type UseGetControlsRegisterArgs = {
  parentId?: string;
} & DashboardFilterArgs<ControlDashboardDateField>;

/**
 * Fetches controls with automatic entity filtering for both GraphQL and tRPC
 * paths. Entity filtering uses useEntityFilter context internally — consumers
 * don't need to pass entityIds. Dashboard filters (COI-691) are all optional.
 */
export const useGetControlsRegister = (params: {
  queryArgs: UseGetControlsRegisterArgs;
  shouldSkip?: boolean;
  fetchPolicy?: GraphQLFetchPolicy;
  suppressErrorNotification?: boolean;
}) => {
  const { entityIds } = useEntityFilter();

  return useGetControlsRegisterBase({
    queryArgs: {
      ...params.queryArgs,
      entityIds: entityIds?.length ? entityIds : undefined,
    },
    shouldSkip: params.shouldSkip,
    fetchPolicy: params.fetchPolicy,
    suppressErrorNotification: params.suppressErrorNotification,
  });
};

/**
 * Maps TRPC control data to match the GraphQL query structure
 * Exported for use in data sources
 */
export function mapTrpcControlsToGraphQL(
  trpcData: ControlRegisterResponse | undefined
): StrictMappedGraphQLSelection<GetControlsQuery> | undefined {
  if (!trpcData) {
    return undefined;
  }

  return {
    control: trpcData.map((control) => mapTrpcControlToGraphQL(control)),
  };
}
